import { CsvDataType, CsvNumberFormat } from '../types/csv.types'
import { isNullLike } from './nullDetector'
import { detectNumberFormat } from './numberFormatDetector'
import { detectDateFormat } from './dateFormatDetector'

const BOOLEAN_TRUE = new Set(['true', 'yes', 'y', 't'])
const BOOLEAN_FALSE = new Set(['false', 'no', 'n', 'f'])
const NUMERIC_ALLOWED = /^[\d., ]+$/
const HAS_DIGIT = /\d/

export interface TypeDetectionResult {
    type: CsvDataType
    numberFormat?: CsvNumberFormat
    dateFormat?: string
    isAmbiguousDate?: boolean
}

function isNumericLike(raw: string): boolean {
    const v = raw.trim().replace(/^[-+]/, '')
    if (!v) return false
    if (!NUMERIC_ALLOWED.test(v)) return false
    return HAS_DIGIT.test(v)
}

function isBooleanToken(raw: string): boolean {
    const lower = raw.trim().toLowerCase()
    return BOOLEAN_TRUE.has(lower) || BOOLEAN_FALSE.has(lower)
}

function hasBooleanWord(values: readonly string[]): boolean {
    return values.some((v) => {
        const lower = v.trim().toLowerCase()
        return lower === 'true' || lower === 'false' || lower === 'yes' || lower === 'no'
    })
}

function hasFractionalPart(raw: string, fmt: CsvNumberFormat): boolean {
    if (!fmt.decimalSeparator) return false
    const idx = raw.lastIndexOf(fmt.decimalSeparator)
    if (idx < 0) return false
    const after = raw.length - idx - 1
    return after >= 1 && after <= 8
}

function detectNumericBranch(nonNull: readonly string[]): TypeDetectionResult | null {
    if (nonNull.length < 2) return null
    if (!nonNull.every(isNumericLike)) return null

    const numberFormat = detectNumberFormat(nonNull.slice(0, 100))
    const hasDecimal = nonNull.some((v) => hasFractionalPart(v.trim(), numberFormat))
    return { type: hasDecimal ? 'float' : 'integer', numberFormat }
}

// Determines the most likely data type for a column based on a sample of
// non-null values. Requires at least 2 non-null values for a strong signal;
// falls back to 'string' when the sample is empty or inconclusive.
export function detectColumnType(sampleValues: readonly string[]): TypeDetectionResult {
    const nonNull = sampleValues.filter((v) => !isNullLike(v))
    if (nonNull.length === 0) return { type: 'string' }

    if (nonNull.length >= 2 && nonNull.every(isBooleanToken) && hasBooleanWord(nonNull)) {
        return { type: 'boolean' }
    }

    const dateResult = detectDateFormat(nonNull)
    if (dateResult.format) {
        return {
            type: dateResult.isDatetime ? 'datetime' : 'date',
            dateFormat: dateResult.format,
            isAmbiguousDate: dateResult.isAmbiguous,
        }
    }

    const numeric = detectNumericBranch(nonNull)
    if (numeric) return numeric

    return { type: 'string' }
}
